import { Injectable } from '@angular/core';
import { SNACKBAR_DURATION_LONG, SNACKBAR_DURATION_MID, SNACKBAR_DURATION_SHORT } from '@app/_helpers';
import { CustomToasterComponent } from '@app/_shared/modules/custom-toaster/custom-toaster.component';
import { CustomToasterConfig } from '@app/_shared/modules/custom-toaster/custom-toaster.interface';
import { CUSTOM_TOASTR_TYPE } from '@app/_shared/shared.enums';
import { ToastrIconType } from '@app/_shared/shared.structs';
import { FixWithAiToastr } from '@app/modules/trading/modules/create-with-ai/chat.interface';
import { FixWithAiToastComponent } from '@app/modules/trading/modules/create-with-ai/fix-with-ai-toast/fix-with-ai-toast.component';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NgxToastrService {
  groupNotification: boolean;
  onTapSubscription: Subscription;
  onCloseButtonSubscription: Subscription;
  // Tracks visible downtime toast so parallel SERVICE_UNAVAILABLE responses show only one.
  private isServiceUnavailableToasterVisible = false;

  constructor(
    private toastr: ToastrService,
  ) { }


  DEFAULT_TIMEOUT = 30000;
  MAXIMUM_ALERTS = 3;
  alertPersistence = [];
  count = 0;
  groupAlert: ActiveToast<any>;

  // Use simpleCustomToaster bu passing simpleToaster as false
  // openActionable(value: string) {
  //   if (this.count++ < this.MAXIMUM_ALERTS) {
  //     this.openActionableToastr(value);
  //   } else {

  //     this.alertPersistence.push(value);
  //     // If this message is coming for the first time create an alert
  //     if (!this.groupNotification) {
  //       this.groupAlert = this.toastr.info(
  //         `You have 1 more notification(s). Click to expand.`, '',
  //         { closeButton: true, tapToDismiss: true, disableTimeOut: true, });
  //       // Binding it with the on tap handler which deals with ungrouping the alerts
  //       this.updateSubscriptions();
  //     } else {
  //       this.updateGroupAlertLabel();
  //     }
  //     this.groupNotification = true;
  //   }
  // }

  updateGroupAlertLabel() {
    // Updating the alert message until the group isn't clicked.
    const groupComponent = this.groupAlert.toastRef.componentInstance;
    groupComponent.message = `You have ${this.alertPersistence.length} more notification(s). Click to expand.`;
  }

  updateSubscriptions() {
    this.onTapSubscription?.unsubscribe();
    this.onCloseButtonSubscription?.unsubscribe();
    this.onTapSubscription = this.groupAlert.onTap.subscribe(this.onTapGroupHandler.bind(this));
    this.onCloseButtonSubscription = this.groupAlert.onHidden.subscribe(this.onCloseGroupHandler.bind(this));
  }

  onCloseGroupHandler() {
    this.count -= this.alertPersistence.length;
    this.alertPersistence = [];
    this.groupNotification = false;
  }

  /*
    Handles the alerts tapping  and recursively add more alerts
    if alerts are greater than the maximum amount.
  */
  onTapGroupHandler(removeAlertCount) {
    const popAlertsSum = removeAlertCount || Math.min(this.MAXIMUM_ALERTS, this.alertPersistence.length);
    /*
      Once tapped, pop all the alerts from the array if the alerts are less than the maximum possible alerts.
      If the alerts in the array are more than the maximum alerts, pop maximum number of alerts and recursively
      create new grouped alerts.
      For instance if there are 8 alerts and the maximum possible alerts are 3, On Tapping the group alert,
      3 alerts will pop and the remaining 5 will be a part of a new Group alert and so on.
    */
    for (let index = 0; index < popAlertsSum; index++) {
      const element = this.alertPersistence.shift();
      this.openActionableToastr(element);
    }
    if (this.alertPersistence.length == 0) {
      this.groupNotification = false;
      this.toastr.clear(this.groupAlert.toastId);
    } else if (!removeAlertCount) {
      // Create a new alert if there are still pending alerts
      this.groupAlert = this.toastr.info(
        `You have ${this.alertPersistence.length} more notification(s). Click to expand.`, '',
        { closeButton: true, tapToDismiss: true, disableTimeOut: true, });
      this.updateSubscriptions();
    } else {
      this.updateGroupAlertLabel();
    }
  }

  openActionableToastr(value) {
    const toastr: ActiveToast<any> = this.toastr.info(value, '', { closeButton: true, tapToDismiss: true, disableTimeOut: true });
    toastr.onHidden.subscribe(this.onCloseAlertHandler.bind(this));
    // Manually clearing the actionable toastr
    setTimeout(() => {
      this.toastr.clear(toastr.toastId);
    }, this.DEFAULT_TIMEOUT);
    return toastr;
  }

  onCloseAlertHandler() {
    this.count = this.count >= 0 ? this.count - 1 : 0;
    /*
      Everytime the normal alert is closed either automatically or manually, pop one alert from the
       alerts array and show the alert
    */
    if (this.alertPersistence.length > 0) {
      this.onTapGroupHandler(1);
    }
  }

  // Use simpleCustomToaster function instead of open
  // open(value: string, duration = 5000, options?: any) {
  //   const toastr = this.toastr.info(value, '', { timeOut: duration, ...options });
  //   return toastr.toastId;
  // }

  // Custom Component toastr
  customToaster(toastConfig: CustomToasterConfig, duration = 5000) {
    const toastr = this.toastr.info('', '', { toastComponent: CustomToasterComponent });
    toastConfig.toastId = toastr.toastId;
    toastr.toastRef.componentInstance.toastData = toastConfig;
    // Close toastr after duration completed
    if (duration) {
      setTimeout(() => {
        this.toastr.remove(toastr.toastId);
      }, duration)
    }

    return toastr.toastId;
  }

  fixWithAiToastr(toastrConfig: FixWithAiToastr, duration: number = 5000) {
    const toastr = this.toastr.info('', '', { toastComponent: FixWithAiToastComponent });
    toastrConfig.toastId = toastr.toastId;
    toastr.toastRef.componentInstance.toastData = toastrConfig;
    if (duration) {
      setTimeout(() => {
        this.toastr.remove(toastr.toastId);
      }, duration)
    }

    return toastr.toastId;
  }

  // Clickable custom snackbar
  clickableCustomToaster(title: string, btnText?: string, route?: any, description?: string, portfolioId?: string, uniquePortfolioID?: string, removeAfterDuration: boolean = false) {
    if (title) {
      return this.customToaster({
        toastClass: "clickable-snackbar",
        title: title,
        message: description,
        toastrType: CUSTOM_TOASTR_TYPE.clickableToastr,
        ...(btnText && {
          button: {
            desc: btnText,
            link: route
          }
        }),
        iconDetail: {
          iconClass: "negative-toast",
          icon: ToastrIconType.error
        },
        ...(portfolioId && { portfolioId: portfolioId }),
        ...(uniquePortfolioID && { uniquePortfolioID: uniquePortfolioID })
      }, removeAfterDuration ? null : SNACKBAR_DURATION_LONG);
    }

  }

  // UT-8139: Service downtime toast — suppress duplicates while parallel APIs fail during outage.
  serviceUnavailableToaster(title: string) {
    if (!title || this.isServiceUnavailableToasterVisible) {
      return;
    }
    this.isServiceUnavailableToasterVisible = true;
    const toastConfig: CustomToasterConfig = {
      toastClass: "warning-bg",
      title: title,
      toastrType: CUSTOM_TOASTR_TYPE.detailedToastr,
      iconDetail: {
        iconClass: "warning-toast",
        icon: ToastrIconType.error
      },
    };
    const toastr = this.toastr.info('', '', { toastComponent: CustomToasterComponent });
    toastConfig.toastId = toastr.toastId;
    toastr.toastRef.componentInstance.toastData = toastConfig;
    toastr.onHidden.pipe(take(1)).subscribe(() => {
      this.isServiceUnavailableToasterVisible = false;
    });

    setTimeout(() => {
      this.toastr.remove(toastr.toastId);
    }, SNACKBAR_DURATION_MID);
  }

  // Simple custom snackbar
  simpleCustomToaster(title: string, simpleToaster = true) {
    if (title) {
      return this.customToaster({
        toastClass: simpleToaster ? "simple-snackbar" : 'warning-bg',
        title: title,
        toastrType: simpleToaster ? CUSTOM_TOASTR_TYPE.simpleToastr : CUSTOM_TOASTR_TYPE.detailedToastr,
        iconDetail: {
        iconClass: simpleToaster ? "positive-toast" : "warning-toast",
          icon: simpleToaster ? ToastrIconType.info : ToastrIconType.error
        },
      }, simpleToaster ? SNACKBAR_DURATION_SHORT : SNACKBAR_DURATION_MID);
    }
  }

  forceClose(toastrId?: number) {
    this.toastr.remove(toastrId);
  }
}
